import { AxiosResponse } from "axios";
import { axiosInstance } from "../../services/v2/axiosInstance";

/*
	Horarios sugeridos y regreso a clases con fecha (Actualizaciones v2, fase E, 2026-10-29).
	GET  enrollmentUpdates/_suggestions?enrollmentId=&mode=change|reentry|merge&excludeScheduleId=1,2
	GET  enrollmentUpdates/_returnPlans?enrollmentId=|studentId=
	PUT  enrollmentUpdates/_cancelReturn {planId, note}
*/

export type ModoSugerencias = "change" | "reentry" | "merge";

/** Un horario sugerido. Compatible con TargetSchedule (id, name, periodName, mentorName, startDate, enrolled, limit, isFull, classesGiven, sessions). */
export interface SugerenciaHorario {
	id: number;
	name: string;
	label: string;
	levelId: number;
	levelName: string | null;
	periodId: number;
	periodName: string | null;
	statusId: number;
	mentorName: string | null;
	startDate: string | null;
	endDate: string | null;
	/** Solo informativo: el cupo no ordena. */
	enrolled: number;
	limit: number;
	isFull: boolean;
	classesGiven: number;
	classesPerWeek: number;
	dueDay: number;
	/** "Sáb 7:00 am a 11:00 am" */
	sessions: string;
	days: number[];
	dayType: "weekend" | "weekday" | "mixed";
	/** Y-m-d de la próxima clase (hora del plantel); null si no tiene fechas. */
	nextClass: string | null;
	/** "HH:mm" */
	nextClassTime: string | null;
	nextClassEstimated: boolean;
	/** Chips: «Mismo Periodo», «Siguiente Periodo», «Mismo Día, Otra Hora», «Fin de Semana»… */
	reasons: string[];
	/** Tomadas por ella menos dadas por el grupo: + adelante (sesiones de más), − atrás (por reponer). */
	sessionsDelta: number;
	deltaLabel: string;
	currentSession: number;
	daysOut: number | null;
	/** La próxima clase cae a más de returnThresholdDays: se ofrece Pausar Hasta / Clases de Cortesía. */
	needsReturnPlan: boolean;
	samePeriod: boolean;
	nextPeriod: boolean;
	rank: number;
}

export interface SugerenciasDTO {
	enrollmentId: number;
	mode: ModoSugerencias;
	today: string;
	returnThresholdDays: number;
	sessionsTaken: number;
	floatingDays?: number | null;
	reference: { scheduleId: number; name: string; label: string; periodName: string | null; sessions: string; dayType: string } | null;
	suggestions: SugerenciaHorario[];
	/** Los que tienen clase pronto, para Clases de Cortesía (quitar el destino elegido). */
	courtesy: SugerenciaHorario[];
}

export interface HorarioCorto { id: number; name: string; label: string; periodName: string | null; }

export interface RegresoProgramadoDTO {
	id: number;
	enrollmentId: number;
	updateId: number | null;
	kind: "pause" | "courtesy";
	statusId: 0 | 1 | 2;
	statusLabel: string | null;
	returnDate: string;
	returnDateLabel: string;
	returnTime: string | null;
	target: HorarioCorto | null;
	courtesy: HorarioCorto | null;
	firstDueDate: string | null;
	monthsShifted: number;
	shiftedTuitions: number;
	lastError: string | null;
	attempts: number;
	doneAt: string | null;
	cancelledAt: string | null;
	cancelNote: string | null;
	waStatus: string | null;
	message?: string;
}

/** Lo que va en options.regreso de una actualización. */
export interface RegresoOpcion { mode: "pause" | "courtesy"; targetScheduleId?: number; courtesyScheduleId?: number; }

const base = (campusId: number) => `/admin/campus/${campusId}/enrollmentUpdates`;

export const sugerenciasApi = {
	sugerencias: (campusId: number, enrollmentId: number, mode: ModoSugerencias = "change", excluir: number[] = []): Promise<AxiosResponse<SugerenciasDTO>> =>
		axiosInstance.get<SugerenciasDTO>(`${base(campusId)}/_suggestions?enrollmentId=${enrollmentId}&mode=${mode}${excluir.length ? "&excludeScheduleId=" + excluir.join(",") : ""}`),
	regresos: (campusId: number, filtro: { enrollmentId?: number; studentId?: number }): Promise<AxiosResponse<RegresoProgramadoDTO[]>> =>
		axiosInstance.get<RegresoProgramadoDTO[]>(`${base(campusId)}/_returnPlans?` + (filtro.enrollmentId ? `enrollmentId=${filtro.enrollmentId}` : `studentId=${filtro.studentId}`)),
	cancelarRegreso: (campusId: number, planId: number, note: string): Promise<AxiosResponse<RegresoProgramadoDTO>> =>
		axiosInstance.put<RegresoProgramadoDTO>(`${base(campusId)}/_cancelReturn`, { planId, note })
};

const DIAS = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

/** "sábado 7 de noviembre" (Y-m-d, día de calendario). */
export const fechaLarga = (ymd: string | null | undefined): string => {
	if (!ymd) { return ""; }
	const d = new Date(ymd.slice(0, 10) + "T12:00:00");
	if (isNaN(d.getTime())) { return ymd; }
	return `${DIAS[(d.getDay() + 6) % 7]} ${d.getDate()} de ${MESES[d.getMonth()]}`;
};

/** "7:00 am" desde "07:00". */
export const hora12 = (hhmm: string | null | undefined): string => {
	if (!hhmm) { return ""; }
	const [h, m] = hhmm.split(":").map((x) => Number(x));
	if (isNaN(h)) { return hhmm; }
	return `${h % 12 || 12}:${String(m || 0).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
};

/** «Regresa a Clases el sábado 7 de noviembre, 7:00 am» (o «Sin Fecha de Clase»). */
export const regresaEl = (s: { nextClass: string | null; nextClassTime?: string | null }): string =>
	s.nextClass ? `Regresa a Clases el ${fechaLarga(s.nextClass)}${s.nextClassTime ? ", " + hora12(s.nextClassTime) : ""}` : "Sin Fecha de Clase";

/** "Hoy", "Mañana", "En 9 días". */
export const enDias = (n: number | null | undefined): string => n === null || n === undefined ? "" : n <= 0 ? "Hoy" : n === 1 ? "Mañana" : `En ${n} Días`;
